/**
 * Error normalizer for consistent error handling across the application
 * Maps various backend error formats to a standardized frontend format
 */

export const normalizeError = (error) => {
    const response = error?.response;
    const status = response?.status || 0;
    const data = response?.data || {};
    
    // Extract error details from different possible formats
    // FastAPI typically returns 'detail' (singular) as an array for validation errors
    const details = data.detail || data.details || data.errors || data.error?.details || [];
    const errorMessage = data.message || data.error?.message || data.error;
    
    // Get first detail message for fallback
    const firstDetailMsg = Array.isArray(details) && details.length > 0
        ? (details[0]?.msg || details[0]?.message || details[0]) 
        : '';
    
    // Base normalized error structure
    const normalized = {
        status: status,
        code: data.error?.code || data.code || 'ERROR',
        message: '',
        details: details,
        originalError: error
    };
    
    // Status-specific error handling
    switch (status) {
        case 401:
            // Unauthorized - use backend message if available, otherwise default
            normalized.message = errorMessage || "Email or password is incorrect.";
            break;
            
        case 403:
            // Forbidden - RBAC permission errors come in data.detail from FastAPI
            // Backend format: {"detail": "User lacks required permission: {permission_name}"}
            const forbiddenDetail = typeof data.detail === 'string' ? data.detail : null;
            normalized.message = forbiddenDetail || errorMessage || "Access denied. You don't have permission for this action.";
            break;
            
        case 404:
            // Not found
            normalized.message = errorMessage || "Resource not found.";
            break;
            
        case 422:
            // Validation error - handle field-level errors
            if (details && Array.isArray(details) && details.length > 0) {
                // For 422 errors, return details for field-level handling
                normalized.message = errorMessage || "Validation failed. Please check your input.";
                // Convert details to field map if needed
                if (details[0]?.loc) {
                    // FastAPI validation error format
                    const fieldErrors = {};
                    details.forEach(validationError => {
                        const field = extractFieldNameFromLocation(validationError.loc);
                        const message = createUserFriendlyValidationMessage(validationError);
                        if (!fieldErrors[field]) fieldErrors[field] = [];
                        fieldErrors[field].push(message);
                    });
                    normalized.fieldErrors = fieldErrors;
                } else {
                    // Handle other validation error formats
                    const fieldErrors = {};
                    details.forEach((error, index) => {
                        const message = extractErrorMessage(error);
                        const field = error.field || `field_${index}`;
                        if (!fieldErrors[field]) fieldErrors[field] = [];
                        fieldErrors[field].push(message);
                    });
                    normalized.fieldErrors = fieldErrors;
                }
            } else {
                normalized.message = errorMessage || extractErrorMessage(firstDetailMsg) || "Validation error.";
            }
            break;
            
        case 429:
            // Rate limited
            normalized.message = errorMessage || "Too many requests. Please try again later.";
            break;
            
        case 500:
        case 502:
        case 503:
        case 504:
            // Server errors
            normalized.message = errorMessage || firstDetailMsg || "Server error. Please try again later.";
            break;
            
        case 0:
            // Network error (no response)
            normalized.message = "Network error. Please check your connection.";
            break;
            
        default:
            // Generic error handling
            if (status >= 400 && status < 500) {
                // Client errors
                normalized.message = errorMessage || firstDetailMsg || "Invalid request. Please check your input.";
            } else if (status >= 500) {
                // Server errors
                normalized.message = errorMessage || firstDetailMsg || "Server error. Please try again later.";
            } else {
                // Unknown or network error
                normalized.message = errorMessage || firstDetailMsg || "Something went wrong.";
            }
    }
    
    return normalized;
};

/**
 * Check if error is a validation error with field details
 */
export const isValidationError = (error) => {
    const status = error?.response?.status;
    return status === 422;
};

/**
 * Extract field errors from validation error for form handling
 */
export const getFieldErrors = (error) => {
    if (!isValidationError(error)) {
        return {};
    }
    
    const normalized = normalizeError(error);
    return normalized.fieldErrors || {};
};

/**
 * Extract error message from error object or string
 * Safely handles FastAPI validation error objects
 */
export const extractErrorMessage = (error) => {
    // If it's already a string, return it
    if (typeof error === 'string') {
        return error;
    }
    
    // If it's null or undefined, return empty string
    if (!error) {
        return '';
    }
    
    // If it's an array, get the first error
    if (Array.isArray(error)) {
        if (error.length === 0) return '';
        return extractErrorMessage(error[0]);
    }
    
    // If it's a FastAPI validation error object with structure {type, loc, msg, input, ctx, url}
    if (typeof error === 'object') {
        // Try to extract message from FastAPI validation error
        if (error.msg) {
            return error.msg;
        }
        
        // Try other common error message fields
        if (error.message) {
            return error.message;
        }
        
        if (error.detail) {
            return error.detail;
        }
        
        if (error.error) {
            return extractErrorMessage(error.error);
        }
        
        // If it's an object but no recognizable message field, convert to string
        if (error.toString && typeof error.toString === 'function' && error.toString() !== '[object Object]') {
            return error.toString();
        }
        
        // Last resort: return a generic message
        return 'Invalid input';
    }
    
    // For any other type, try to convert to string
    return String(error);
};

/**
 * Get user-friendly message for display
 */
export const getErrorMessage = (error) => {
    const normalized = normalizeError(error);
    return normalized.message;
};

/**
 * Check if error should show toast notification
 * (e.g., validation errors might be handled inline instead)
 */
export const shouldShowToast = (error) => {
    const status = error?.response?.status;
    
    // Don't show toast for validation errors that should be handled inline
    if (status === 422) {
        return false;
    }
    
    return true;
};

/**
 * Extract form field name from FastAPI validation error location array
 * Handles nested paths like ["body", "demographics", "emirates_id"] -> "emirates_id"
 * @param {Array} loc - FastAPI location array
 * @returns {string} Form field name
 */
export const extractFieldNameFromLocation = (loc) => {
    if (!Array.isArray(loc) || loc.length === 0) {
        return 'unknown';
    }
    
    // Common patterns in FastAPI location arrays:
    // ["body", "field_name"] -> "field_name"
    // ["body", "demographics", "field_name"] -> "field_name" 
    // ["body", "nested", "deeply", "field_name"] -> "field_name"
    
    // Always take the last element as the field name
    const fieldName = loc[loc.length - 1];
    
    // Handle special cases where the field name might need mapping
    const fieldMappings = {
        // Add any specific field mappings if needed
        // 'api_field_name': 'form_field_name'
    };
    
    return fieldMappings[fieldName] || fieldName;
};

/**
 * Create user-friendly error messages for validation patterns
 * @param {Object} validationError - FastAPI validation error object
 * @returns {string} User-friendly error message
 */
export const createUserFriendlyValidationMessage = (validationError) => {
    const { type, msg, ctx, loc } = validationError || {};
    const fieldName = extractFieldNameFromLocation(loc);
    
    // Create user-friendly messages based on validation type
    switch (type) {
        case 'string_pattern_mismatch':
            if (fieldName === 'emirates_id' || fieldName.includes('emirates')) {
                return 'Emirates ID must be 15 digits starting with 784 (e.g., 784123456789012)';
            }
            if (fieldName.includes('phone') || fieldName.includes('contact')) {
                return 'Phone number must be in international format (e.g., +971501234567)';
            }
            if (ctx?.pattern) {
                return `Invalid format. Expected pattern: ${ctx.pattern}`;
            }
            return msg || 'Invalid format';
            
        case 'value_error.missing':
            return `${fieldName.replace('_', ' ')} is required`;
            
        case 'type_error.string':
            return 'Must be a text value';
            
        case 'type_error.integer':
            return 'Must be a number';
            
        case 'value_error.email':
            return 'Please enter a valid email address';
            
        case 'value_error.url':
            return 'Please enter a valid URL';
            
        case 'value_error.date':
            return 'Please enter a valid date';
            
        default:
            // Fallback to the original message or create a generic one
            return msg || `Invalid ${fieldName.replace('_', ' ')}`;
    }
};